import { request } from './api';
import { API_ENDPOINTS } from './endpoints';
import { getUserPool } from './cognito';

/**
 * Agent Session API Service
 * Handles all API calls related to agent chat sessions
 */

/**
 * Get the current user's Cognito JWT token
 * @returns {Promise<string|null>} JWT token or null if not logged in
 */
const getToken = async () => {
  return new Promise((resolve) => {
    const cognitoUser = getUserPool().getCurrentUser();
    if (cognitoUser) {
      cognitoUser.getSession((err, result) => {
        if (err) {
          return resolve(null);
        }
        return resolve(result?.idToken?.jwtToken);
      });
    } else {
      resolve(null);
    }
  });
};

/**
 * List all agent sessions for the current user
 * @returns {Promise<Array>} Array of session objects
 * Response format: { data: [{ session_id, name, is_favorite, is_archived, created_at, updated_at }] }
 */
export const listAgentSessions = async () => {
  const token = await getToken();
  const headers = {};

  if (token) {
    headers['Authorization'] = token;
  }

  const response = await request({
    endpoint: API_ENDPOINTS.AGENT_LIST_SESSIONS,
    headers
  });
  return response.data || [];
};

/**
 * Get conversation history for a specific session
 * @param {string} sessionId - The session ID
 * @returns {Promise<Array>} Array of session messages
 * Response format: { data: [{ message, created_at, ... }] }
 */
export const listAgentSessionHistory = async (sessionId) => {
  if (!sessionId) {
    throw new Error('sessionId is required');
  }

  const token = await getToken();
  const headers = {};

  if (token) {
    headers['Authorization'] = token;
  }

  const response = await request({
    endpoint: API_ENDPOINTS.AGENT_LIST_SESSION_HISTORY,
    params: { sessionId },
    headers
  });
  return response.data || [];
};

/**
 * Update session metadata (rename, favorite, archive)
 * @param {string} sessionId - The session ID
 * @param {Object} updates - Updates to apply
 * @param {string} [updates.name] - New session name
 * @param {boolean} [updates.is_favorite] - Toggle favorite status
 * @param {boolean} [updates.is_archived] - Toggle archive status
 * @returns {Promise<Object>} Updated session data
 * Response format: { data: [{ session_id, name, is_favorite, is_archived, updated_at }] }
 */
export const updateAgentSession = async (sessionId, updates) => {
  if (!sessionId) {
    throw new Error('sessionId is required');
  }

  if (!updates || Object.keys(updates).length === 0) {
    throw new Error('updates object is required');
  }

  const token = await getToken();
  const headers = {};

  if (token) {
    headers['Authorization'] = token;
  }

  const response = await request({
    endpoint: API_ENDPOINTS.AGENT_UPDATE_SESSION,
    params: { sessionId },
    body: updates,
    headers
  });
  return response.data?.[0] || null;
};

/**
 * Delete a session completely
 * @param {string} sessionId - The session ID
 * @returns {Promise<Object>} Success message
 * Response format: { message: "Deleted." }
 */
export const deleteAgentSession = async (sessionId) => {
  if (!sessionId) {
    throw new Error('sessionId is required');
  }

  const token = await getToken();
  const headers = {};

  if (token) {
    headers['Authorization'] = token;
  }

  const response = await request({
    endpoint: API_ENDPOINTS.AGENT_DELETE_SESSION,
    params: { sessionId },
    headers
  });
  return response;
};

/**
 * Search across sessions and messages using full-text search
 * NOTE: This API endpoint is no longer called from the UI
 * Previous popup modal used this for content search
 * Current /conversations page uses client-side title filtering instead
 *
 * @param {string} query - Search query string
 * @returns {Promise<Array>} Array of search results with similarity scores
 * Response includes:
 * - session_name matches
 * - message content matches
 * - similarity_score for ranking
 * - source field (session_name or message)
 */
export const searchAgentSessions = async (query) => {
  if (!query) {
    throw new Error('query is required');
  }

  const token = await getToken();
  const headers = {};

  if (token) {
    headers['Authorization'] = token;
  }

  const response = await request({
    endpoint: API_ENDPOINTS.AGENT_SEARCH_SESSIONS,
    params: { q: query },
    headers
  });
  return response.data || [];
};
